import React from 'react';

import { Icon, IconTypes } from '@eika/icon';
import { ListPanel } from '@eika/link-panel';

import piwik from '@/piwik/piwik';
import { useAccount } from '@/react-query/hooks/query/use-account';

import { useSession } from '@components/app';
import { useConfigLink, useLinkProducer } from '@domain/app';
import { Card } from '@generated-types';
import useText from '@hooks/use-text';
import { userCanBlockCard } from '@utils/block-card-utils';
import { isHardBlocked, isSoftBlocked } from '@utils/card-utils';
import {
    getBlockSelectedCardUrl,
    getHardBlockUrl,
    getReorderSelectedCardUrl,
    getSoftBlockUrl,
} from '@utils/url/get-url-utils';

interface BlockReplaceProps {
    card: Card;
}

const BlockReplace = ({ card }: BlockReplaceProps) => {
    const text = useText();
    const linkHardBlockCardSelectReason = useConfigLink('linkHardBlockCardSelectReason');
    const { user } = useSession();
    const account = useAccount(card)?.data;
    const linkProducer = useLinkProducer();

    if (!account || !user) {
        return null;
    }

    if (!card.allowedOperations.hardBlock && !card.allowedOperations.softBlock) {
        return null;
    }

    if (!userCanBlockCard(card, user, account)) {
        return null;
    }

    const getFormUrlBasedOnCard = () => {
        if (isSoftBlocked(card)) {
            return getHardBlockUrl(linkProducer)(card, true);
        }

        if (isHardBlocked(card)) {
            return getReorderSelectedCardUrl(card, linkHardBlockCardSelectReason);
        }
        if (!card.allowedOperations.hardBlock) {
            return getSoftBlockUrl(linkProducer)(card);
        }

        return getBlockSelectedCardUrl(linkProducer)(card, true);
    };

    return (
        <ListPanel
            data-track-id={piwik.cardDetails.cardFunctionalityButtonSperrKort.dataTrackId}
            data-track-linkname={piwik.cardDetails.cardFunctionalityButtonSperrKort.dataTrackLinkname}
            id="link-card-block-replace"
            url={getFormUrlBasedOnCard()}
            prefixContent={<Icon type={IconTypes.SPERR_KORT_24} />}
            backgroundColor="light"
            icon={IconTypes.PIL_HOEYRE}
            title={text.blockReorderTitle}
        />
    );
};

export default BlockReplace;
